/* eslint-disable no-use-before-define */
import { useCallback, useState } from "react";
import PerfectScrollbar from "react-perfect-scrollbar";
import { useFormik } from "formik";

// @material-ui/core components
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import LinearProgress from "@material-ui/core/LinearProgress";
import Grid from "@material-ui/core/Grid";
import FormGroup from "@material-ui/core/FormGroup";
import FormLabel from "@material-ui/core/FormLabel";
import TextField from "@material-ui/core/TextField";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import MenuItem from "@material-ui/core/MenuItem";

import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import MaskAndValid from "utils/maskAndValid";
import Api from "lib/api/httpRequestHelper";
import moment from "moment";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import DateField from "components/_refactor/fields/date";

const today = moment();

export default function FormIncidentDialog({
  open,
  formType,
  onClose,
  selectedIncident,
}) {
  const [submitting, setSubmitting] = useState(false);
  const snackbar = useSnackbar();
  const [dataResolucao, setDataResolucao] = useState(
    selectedIncident?.DataResolucao &&
      selectedIncident.DataResolucao !== "0001-01-01T00:00:00"
      ? selectedIncident.DataResolucao.slice(0, 10)
      : today.format("YYYY-MM-DD")
  );
  const [dataAbertura, setDataAbertura] = useState(
    selectedIncident?.DataAbertura?.slice(0, 10) || today.format("YYYY-MM-DD")
  );

  const deleteIncident = useCallback(
    async (idIncidente, handleClose) => {
      setSubmitting(true);

      await Api.deleteParams(
        "delete-incident",
        "adminportal",
        "?idIncidente=" + idIncidente.toString(),
        true
      )
        .then((res) => {
          if (res.Success) {
            snackbar.showMessage(`Incidente excluído com sucesso!`, {
              severity: "success",
            });
            setSubmitting(false);
            handleClose({ IdIncidente: idIncidente });
          } else {
            snackbar.showMessage("Ocorreu um erro ao deletar o incidente.", {
              severity: "error",
            });
            setSubmitting(false);
          }
        })
        .catch((error) => {
          snackbar.showMessage(error, {
            severity: "error",
          });
          setSubmitting(false);        });
    },
    [snackbar]
  );

  const submit = useCallback(
    async (values, { resetForm }) => {
      const handleClose = (updatedIncident) => {
        resetForm();
        onClose(updatedIncident);
      };

      const { sistema, descricao, status } = values;
      const updatedIncident = {};

      setSubmitting(true);

      if (formType === "delete") {
        return deleteIncident(selectedIncident?.IdIncidente, handleClose);
      }

      return await Api.postParams(
        formType === "edit" ? "update-incident" : "create-incident",
        "adminportal",
        "?sistema=" +
          sistema +
          "&descricaoOcorrido=" +
          descricao +
          "&status=" +
          status +
          "&dataAbertura=" +
          dataAbertura +
          (dataResolucao && status === "Resolvido"
            ? "&dataResolucao=" + dataResolucao
            : "") +
          (selectedIncident?.IdIncidente
            ? "&idIncidente=" + selectedIncident.IdIncidente.toString()
            : ""),
        true
      )
        .then((res) => {
          if (res.Success) {
            snackbar.showMessage(
              `Incidente ${
                formType === "edit" ? "atualizado" : "criado"
              } com sucesso!`,
              {
                severity: "success",
              }
            );
            setSubmitting(false);
            handleClose({ ...updatedIncident, ...res.Result });
          } else {
            snackbar.showMessage(
              `Ocorreu um erro ao ${
                formType === "edit" ? "atualizar" : "criar"
              } o incidente. Verifique os parâmetros informados.`,
              {
                severity: "error",
              }
            );
            setSubmitting(false);
          }
        })
        .catch((error) => {
          snackbar.showMessage(error, {
            severity: "error",
          });
          setSubmitting(false);        });
    },
    [
      dataAbertura,
      dataResolucao,
      deleteIncident,
      formType,
      onClose,
      selectedIncident?.IdIncidente,
      snackbar,
    ]
  );

  const formik = useFormik({
    initialValues: {
      sistema: selectedIncident?.Sistema || "",
      descricao: selectedIncident?.DescricaoOcorrido || "",

      status: selectedIncident?.Status || "",
    },
    onSubmit: submit,
  });

  const { values, handleChange, handleSubmit, dirty, resetForm } = formik;

  const handleClose = useCallback(
    (updatedIncident) => {
      resetForm();
      onClose(updatedIncident);
    },
    [onClose, resetForm]
  );

  const handleStatusChange = useCallback(
    (event) => {
      const currentStatus = values.status;

      if (
        currentStatus === "Resolvido" &&
        currentStatus !== event.target.value
      ) {
        setDataResolucao(null);
      }

      handleChange(event);
    },
    [handleChange, values.status]
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        maxWidth={"md"}
      >
        <PerfectScrollbar>
          <form onSubmit={handleSubmit}>
            <DialogTitle>
              <span>
                <Typography id="alert-dialog-title" variant="h3" component="h3">
                  {`${
                    formType === "edit"
                      ? "Editar"
                      : formType === "delete"
                      ? "Excluir"
                      : "Criar"
                  } Incidente`}
                </Typography>
              </span>
            </DialogTitle>
            <PerfectScrollbar>
              <DialogContent style={{ padding: "1rem 1.5rem 0" }}>
                {formType === "delete" ? (
                  <Typography variant="h4" style={{ marginBottom: "2rem" }}>
                    Confirmar exclusão do Incidente?
                  </Typography>
                ) : (
                  <>
                    <Grid container>
                      <Grid item xs={12}>
                        <FormGroup>
                          <DateField
                            label="Data de Abertura"
                            value={dataAbertura}
                            onChange={setDataAbertura}
                            disabled={submitting}
                            required
                          />
                        </FormGroup>
                      </Grid>
                      <Grid item xs={12}>
                        <FormGroup>
                          <FormLabel required>Status do Incidente</FormLabel>
                          <FormControl
                            variant="outlined"
                            fullWidth
                            disabled={submitting}
                          >
                            <Select
                              required
                              name="status"
                              value={values.status}
                              onChange={handleStatusChange}
                            >
                              <MenuItem value={"Aberto"}>Aberto</MenuItem>
                              <MenuItem value={"Bloqueado"}>Bloqueado</MenuItem>
                              <MenuItem value={"Com fornecedor"}>
                                Com fornecedor
                              </MenuItem>
                              <MenuItem value={"Resolvido"}>Resolvido</MenuItem>
                            </Select>
                          </FormControl>
                        </FormGroup>
                      </Grid>
                      {values.status === "Resolvido" && (
                        <Grid item xs={12}>
                          <FormGroup>
                            <DateField
                              label="Data de Resolução"
                              value={dataResolucao}
                              onChange={setDataResolucao}
                              disabled={submitting}
                              required
                            />
                          </FormGroup>
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <AutoCompleteField
                          label="Sistema"
                          required
                          disabled={submitting}
                          value={
                            [
                              { value: "APP", label: "APP" },
                              { value: "PORTALB2B", label: "B2B" },
                              { value: "PRATINF", label: "PD" },
                            ].find((opt) => opt.value === values.sistema) ||
                            null
                          }
                          onChange={(option) =>
                            handleChange({
                              target: {
                                name: "sistema",
                                value: option?.value || "",
                              },
                            })
                          }
                          options={[
                            { value: "APP", label: "APP" },
                            { value: "PORTALB2B", label: "B2B" },
                            { value: "PRATINF", label: "PD" },
                          ]}
                          optionLabelKey="label"
                          optionIdKey="value"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormGroup>
                          <FormLabel required>Descrição do Ocorrido</FormLabel>
                          <TextField
                            name="descricao"
                            required
                            fullWidth
                            multiline
                            disabled={submitting}
                            value={values.descricao}
                            variant="outlined"
                            onChange={handleChange}
                            onInput={(e) => {
                              e.target.value = e.target.value
                                .toString()
                                .slice(0, 200);
                            }}
                          />
                        </FormGroup>
                      </Grid>
                    </Grid>
                  </>
                )}
                {submitting && (
                  <div style={{ marginTop: "1rem" }}>
                    <span>{`${
                      formType === "create"
                        ? "Criando"
                        : formType === "edit"
                        ? "Atualizando"
                        : "Deletando"
                    } incidente...`}</span>
                    <LinearProgress />
                  </div>
                )}
              </DialogContent>
            </PerfectScrollbar>
            <DialogActions>
              <Button
                component={Box}
                onClick={handleClose}
                disabled={submitting}
              >
                Cancelar
              </Button>
              {formType === "delete" ? (
                <Button variant="contained" type="submit" disabled={submitting}>
                  Confirmar
                </Button>
              ) : (
                <Button
                  variant="contained"
                  type="submit"
                  disabled={Boolean(
                    submitting ||
                      (formType !== "edit" && !dirty) ||
                      MaskAndValid.isNullOrEmpty(values.sistema) ||
                      MaskAndValid.isNullOrEmpty(values.descricao) ||
                      MaskAndValid.isNullOrEmpty(dataAbertura) ||
                      MaskAndValid.isNullOrEmpty(values.status) ||
                      (dataResolucao &&
                        MaskAndValid.isNullOrEmpty(dataResolucao))
                  )}
                >
                  {formType === "edit" ? "Atualizar" : "Criar"}
                </Button>
              )}
            </DialogActions>
          </form>
        </PerfectScrollbar>
      </Dialog>
    </>
  );
}
